/**
 * Supertrend
 *
 * Trend-following overlay indicator based on ATR.
 * Plots a line above price in a downtrend (red) and below in an uptrend (green).
 * Flips direction when price crosses the band.
 * Default: ATR period=10, multiplier=3.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeATR } from "./atr";

export const supertrendIndicator: IndicatorDefinition = {
  id: "supertrend",
  name: "Supertrend",
  shortName: "ST",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "ATR Period", type: "number", default: 10, min: 1, max: 200 },
    { key: "multiplier", label: "Multiplier", type: "number", default: 3, min: 0.1, max: 20, step: 0.1 },
    { key: "upColor", label: "Up Color", type: "color", default: "#26A69A" },
    { key: "downColor", label: "Down Color", type: "color", default: "#EF5350" },
  ],
  plots: [
    { key: "supertrend", label: "Supertrend", color: "#26A69A", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, multiplier, upColor = "#26A69A", downColor = "#EF5350" } = params;
    const atr = computeATR(candles, period);
    const data: { time: number; value: number; color?: string }[] = [];

    if (candles.length < period) return [{ key: "supertrend", data }];

    // Arrays for tracking bands and direction
    const upperBand: number[] = new Array(candles.length).fill(0);
    const lowerBand: number[] = new Array(candles.length).fill(0);
    const direction: number[] = new Array(candles.length).fill(1); // 1 = uptrend, -1 = downtrend

    // ATR-based SuperTrend band computation.
    // Anti-repaint rule (matches Scalping Trend approach):
    //   - Bands are ratcheted on every bar (including the forming bar) so the
    //     trailing-stop LINE VALUE stays current with live price.
    //   - Direction (buy / sell colour) is ONLY updated on CLOSED bars.
    //   - The forming bar (last candle) always inherits direction from the
    //     previous closed bar, so a signal can never disappear on refresh.
    for (let i = 0; i < candles.length; i++) {
      if (isNaN(atr[i])) continue;

      const hl2 = (candles[i].high + candles[i].low) / 2;
      const basicUpper = hl2 + multiplier * atr[i];
      const basicLower = hl2 - multiplier * atr[i];

      if (i === period - 1) {
        // Initialise: start in downtrend on the first valid ATR bar
        upperBand[i] = basicUpper;
        lowerBand[i] = basicLower;
        direction[i] = -1;
      } else if (i > period - 1) {
        // Ratchet bands: upperBand tightens down, lowerBand tightens up
        upperBand[i] = basicUpper < upperBand[i - 1] || candles[i - 1].close > upperBand[i - 1]
          ? basicUpper : upperBand[i - 1];
        lowerBand[i] = basicLower > lowerBand[i - 1] || candles[i - 1].close < lowerBand[i - 1]
          ? basicLower : lowerBand[i - 1];

        // Anti-repaint: forming bar (last candle) locks to last closed bar's direction.
        // Direction flips are only committed when a bar fully closes.
        const isFormingBar = i === candles.length - 1;
        if (isFormingBar) {
          direction[i] = direction[i - 1];
        } else if (direction[i - 1] === 1) {
          // In uptrend — flip to downtrend if close drops below lowerBand
          direction[i] = candles[i].close < lowerBand[i] ? -1 : 1;
        } else {
          // In downtrend — flip to uptrend if close rises above upperBand
          direction[i] = candles[i].close > upperBand[i] ? 1 : -1;
        }
      }

      const st = direction[i] === 1 ? lowerBand[i] : upperBand[i];

      if (i >= period - 1) {
        data.push({
          time: candles[i].time,
          value: st,
          color: direction[i] === 1 ? upColor : downColor,
        });
      }
    }

    return [{ key: "supertrend", data }];
  },
};
